import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useBusyWhile } from '@/context/busy-context';
import { getApiErrorMessage } from '@utils/api-error';
import type { IApiEnvelope } from '@/types/api';

export interface IUseApiModelResult<TModel> {
    model: TModel;
    isLoading: boolean;
    error: string | null;
}

interface ILoadedState<TModel> {
    url: string | null;
    model: TModel;
    error: string | null;
}

const identity = <TModel, TResults>(results: TResults) => results as unknown as Partial<TModel>;

/**
 * Loads a page model from a GET endpoint and merges it over `defaultModel`, so any field
 * the server omits keeps a safe value. Re-fetches whenever `url` changes; while a fetch is
 * outstanding `isLoading` is true and `model` still holds the previous value.
 *
 * `select` picks the model out of `results` when the server nests it.
 */
export const useApiModel = <TModel, TResults = Partial<TModel>>(
    url: string,
    defaultModel: TModel,
    select: (results: TResults) => Partial<TModel> = identity,
): IUseApiModelResult<TModel> => {
    const [state, setState] = useState<ILoadedState<TModel>>({ url: null, model: defaultModel, error: null });

    const latest = useRef({ defaultModel, select });

    useEffect(() => {
        latest.current = { defaultModel, select };
    });

    useEffect(() => {
        const controller = new AbortController();

        axios
            .get<IApiEnvelope<TResults>>(url, {
                headers: { Accept: 'application/json' },
                signal: controller.signal,
            })
            .then((response) => {
                const { defaultModel: base, select: pick } = latest.current;

                setState({ url, model: { ...base, ...pick(response.data.results) }, error: null });
            })
            .catch((err: unknown) => {
                if (axios.isCancel(err)) return;

                setState({ url, model: latest.current.defaultModel, error: getApiErrorMessage(err, 'Failed to load the page') });
            });

        return () => controller.abort();
    }, [url]);

    const isLoading = state.url !== url;

    useBusyWhile(isLoading);

    return { model: state.model, isLoading, error: state.error };
};
